import toast from 'react-hot-toast'
import { IFoundProductData } from '../../model/IFoundProduct'
import {
  IImportForm,
  IImportFormFullProduct,
  IImportFormProduct,
  IImportFormProductBase,
} from '../../model/IImportForm'
import { getErrorFromCatch, serializeFieldErrors } from '../services/api'
import { IAddImportProductsResponse, importApi } from '../services/import.api'
import { z } from 'zod'
import checkFormStateByZod from '../utils/checkFormStateByZod'
import { IHookApi } from '../../model/IHookApi'
import { IImport } from '../../model/IImport'
import { useState } from 'react'

// --
// -- Zod validation scheme
// --
const findProductScheme = z.object({
  productId: z.string().min(1, 'Please input the Product ID'),
  company: z.string().min(1, 'Please input Source Company'),
} as Record<keyof IImportFormProduct, any>)
// --
const addImportScheme = z.object({} as Record<keyof IImportForm, any>)

export default function useImport() {
  const [addImportProductApi] = importApi.useAddImportProductsMutation()
  const [toFindProductApi] = importApi.useLazyFindProductQuery()
  const [repeatImportApi] = importApi.useRepeatImportMutation()
  const [deleteImportsApi] = importApi.useDeleteImportsMutation()
  const [loading, setLoading] = useState<boolean>(false)

  const toFindProduct = async (
    product: IImportFormProduct,
  ): Promise<IFoundProductData | Partial<Record<keyof IImportFormProduct, string>>> => {
    // -- Client validation form
    const clientErrs = checkFormStateByZod(findProductScheme, product)
    if (clientErrs) return clientErrs

    try {
      setLoading(() => true)
      const data = await toFindProductApi(product).unwrap()
      return data
    } catch (err) {
      toast.error(`${getErrorFromCatch(err)}`, { duration: 8000 })
      return serializeFieldErrors(err) ?? { foundProductError: getErrorFromCatch(err) }
    } finally {
      setLoading(() => false)
    }
  }

  const addImportProducts = async (
    data: IImportForm,
  ): Promise<IHookApi<IImportForm, IAddImportProductsResponse>> => {
    // -- Client validation form
    const clientErrs = checkFormStateByZod(addImportScheme, data)
    if (clientErrs) return clientErrs

    // -- Prepared import form data
    const foundProducts = data.products.reduce(
      (acc, { foundProductId, productId, ...options }) => [
        ...acc,
        ...(foundProductId
          ? [
              {
                productId: foundProductId,
                ...options,
              },
            ]
          : []),
      ],
      [] as IImportFormProductBase[],
    )
    if (!foundProducts?.length) return { error: 'There is no existed products' }

    const preparedForm: IImportFormFullProduct[] = foundProducts.map((foundProduct) => ({
      ...foundProduct,
      configurationType: data.configurationType,
      priceType: data.priceType,
      ...(data?.categoryIds ? { categoryIds: data?.categoryIds } : {}),
      goalServer: 'magento.lan',
    }))

    try {
      setLoading(() => true)
      const data = await addImportProductApi(preparedForm).unwrap()
      return data
    } catch (err) {
      toast.error(`${getErrorFromCatch(err)}`, { duration: 8000 })
      return serializeFieldErrors(err) ?? { foundProductError: getErrorFromCatch(err) }
    } finally {
      setLoading(() => false)
    }
  }

  // -- Repeat error message or null (if all is ok)
  const repeatImport = async (imprt: IImport): Promise<string | null> => {
    try {
      setLoading(() => true)
      await repeatImportApi(imprt.idImport).unwrap()
      return null
    } catch (err) {
      toast.error(`${getErrorFromCatch(err)}`, { duration: 8000 })
      return getErrorFromCatch(err)
    } finally {
      setLoading(() => false)
    }
  }

  const deleteImports = (mode: 'pending' | 'all') => async (): Promise<string | null> => {
    try {
      setLoading(() => true)
      const response = await deleteImportsApi(mode).unwrap()
      if (response?.count === undefined) throw new Error(`Failed to delete ${mode} imports`)
      toast.success(`The ${response.count} ${mode} imports have been deleted successfully`)
      return null
    } catch (err) {
      toast.error(`${getErrorFromCatch(err)}`, { duration: 8000 })
      return getErrorFromCatch(err)
    } finally {
      setLoading(() => false)
    }
  }

  return {
    addImportProducts,
    toFindProduct,
    repeatImport,
    deletePendingImportsHandler: deleteImports('pending'),
    deleteAllImportsHandler: deleteImports('all'),
  }
}
